import { call, put, takeLatest, fork, select } from "redux-saga/effects";
import {
  CREATE_GROUP,
  FETCH_GROUP,
  SAVE_GROUP_VALUES,
} from "../actions/action-types";
import {
  createGroupSuccess,
  setGroup,
  setGroupAttr,
  saveGroupValuesSuccess,
} from "../actions/group-actions";
import { syncEntry } from "../actions/entry-actions";
import { syncUser } from "../actions/user-actions";
import API from "../api";
import { currentUserSelector } from "../selectors/current-user-selector";
import { pendingValuesSelector } from "../selectors/ui-selector.js";
import { fetchGameAndDependents, syncCategories, syncGames } from "./gameSaga";
import {
  get,
  sync,
  update,
  CHILD_CHANGED,
  CHILD_ADDED,
  CHILD_REMOVED,
} from "./firebase-saga";
import { ref, query, orderByChild, equalTo, get as firebaseGet } from "firebase/database";
import { database } from "../firebaseSetup";
import { Map } from "immutable";
import Group from "../models/Group";

export function* createGroup(action) {
  try {
    const currentUser = yield select(currentUserSelector);
    const newGroupId = yield call(API.createGroupId, null);
    const categoriesQuery = query(
      ref(database, "categories"),
      orderByChild("game"),
      equalTo(action.payload.game)
    );
    const response = yield call(firebaseGet, categoriesQuery);
    const categories = response.val();
    const categoryValues = Object.keys(categories).reduce(
      (acc, key) => ({ ...acc, [key]: categories[key].value }),
      {}
    );
    yield call(
      API.createGroup,
      newGroupId,
      action.payload,
      currentUser,
      categoryValues
    );
    yield put(
      createGroupSuccess(
        newGroupId,
        new Group({
          ...action.payload,
          id: newGroupId,
          admin: currentUser.id,
          values: new Map(categoryValues),
        }).toJS()
      )
    );
    window.location.href = `/groups/${newGroupId}`;
  } catch (errors) {
    console.log(errors);
  }
}

export function* watchCreateGroup() {
  yield fork(takeLatest, CREATE_GROUP, createGroup);
}

export function* fetchGroup(action) {
  const { id } = action.payload;
  try {
    // Check if group already exists in store (useful for mock mode)
    const existingGroups = yield select((state) => state.groups);
    const existingGroup = existingGroups.get(id);

    if (existingGroup) {
      // Group already in store, just ensure game and dependents are loaded
      yield call(fetchGameAndDependents, existingGroup.game);
      yield call(syncGroupAndDependents, null);
      return;
    }

    // Group not in store, fetch from Firebase
    const group = yield call(get, "groups", id);
    if (!group) {
      console.log(`Group ${id} not found`);
      return;
    }
    yield put(setGroup(group));
    yield call(fetchGameAndDependents, group.game);
    yield call(syncGroupAndDependents, null);
  } catch (errors) {
    console.log(errors);
  }
}

export function* syncGroup() {
  yield fork(sync, `groups`, {
    [CHILD_ADDED]: setGroupAttr,
    [CHILD_CHANGED]: setGroupAttr,
    [CHILD_REMOVED]: setGroupAttr,
  });
}

export function* syncEntries() {
  yield fork(sync, "entries", {
    [CHILD_ADDED]: syncEntry,
    [CHILD_CHANGED]: syncEntry,
  });
}

export function* syncUsers() {
  yield fork(sync, "users", {
    [CHILD_ADDED]: syncUser,
  });
}

export function* syncGroupAndDependents() {
  // Skip syncing in mock mode - data is already in store
  const isMockMode = process.env.REACT_APP_USE_MOCK_DATA === "true";
  if (isMockMode) {
    return;
  }

  yield fork(syncGroup, null);
  yield fork(syncGames, null);
  yield fork(syncCategories, null);
  yield fork(syncEntries, null);
  yield fork(syncUsers, null);
}

export function* watchFetchGroup() {
  yield fork(takeLatest, FETCH_GROUP, fetchGroup);
}

export function* saveGroupValues(action) {
  try {
    const newValues = yield select(pendingValuesSelector);
    yield call(
      update,
      `groups/${action.payload.groupId}`,
      "values",
      newValues.toJS()
    );
    yield put(saveGroupValuesSuccess(action.payload.groupId, newValues));
  } catch (errors) {
    console.log(errors);
  }
}

export function* watchSaveGroupValues() {
  yield fork(takeLatest, SAVE_GROUP_VALUES, saveGroupValues);
}
